import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { message as antdMessage, notification } from 'antd';
import { fetchByStepExecution, fetchInbox, fetchInboxMeta, sendIntervention } from './chatBotSessionApi';
import { buildCombinedMessages } from './utils';

const DEFAULT_POLL_INTERVAL = 30000; // 30 秒

/**
 * 共用對話狀態 hook。
 *
 * mode: 'stepExecution' | 'inbox'
 * - stepExecution: 依 stepExecutionId 載入 sessions（Modal 用）
 * - inbox: 載入全域 inbox（ChatBotInboxPage 用），並啟動後台輪詢
 *
 * pollIntervalMs: inbox 模式的輪詢間隔（ms），預設 30000
 */
const useChatBotConversation = ({
  mode = 'stepExecution',
  stepExecutionId,
  inboxParams = {},
  loadFailedFallback = '載入對話失敗',
  enabled = true,
  pollIntervalMs = DEFAULT_POLL_INTERVAL
} = {}) => {
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [stepInfo, setStepInfo] = useState(null);
  const [inboxMeta, setInboxMeta] = useState({ total: 0, page: 1, pageSize: 50 });
  const [activeSessionId, setActiveSessionIdRaw] = useState(null);
  const [interventionMessages, setInterventionMessages] = useState({});
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);

  // ──────────────────────────────────────────────
  // Unread tracking (inbox mode)
  // ──────────────────────────────────────────────
  // seenMessageCountsRef[sessionId] = 用戶最後一次「看到」的訊息數
  const seenMessageCountsRef = useRef({});
  const [unreadCounts, setUnreadCounts] = useState({}); // { [sessionId]: number }
  // 目前 active session 是否有尚未重載的新訊息
  const [newMessagesForActive, setNewMessagesForActive] = useState(false);
  // 最近新增的 session IDs（用於入場動畫，8 秒後自動清除）
  const [newSessionIds, setNewSessionIds] = useState(new Set());

  // 快取最新 sessions 供 poll closure 使用
  const sessionsRef = useRef([]);

  // 快取最新 sessions 供 poll closure 使用
  useEffect(() => { sessionsRef.current = sessions; }, [sessions]);

  // 快取最新 inboxParams 供 poll closure 使用（避免 stale closure）
  const inboxParamsRef = useRef(inboxParams);
  useEffect(() => { inboxParamsRef.current = inboxParams; }, [inboxParams]);

  // 快取最新 activeSessionId 供 poll closure 使用
  const activeSessionIdRef = useRef(null);
  const inputTextRef = useRef('');
  useEffect(() => { activeSessionIdRef.current = activeSessionId; }, [activeSessionId]);
  useEffect(() => { inputTextRef.current = inputText; }, [inputText]);

  // ──────────────────────────────────────────────
  // 全量載入（初始 + 手動刷新）
  // ──────────────────────────────────────────────
  const loadSessions = useCallback(async () => {
    if (!enabled) return;
    if (mode === 'stepExecution' && !stepExecutionId) return;
    setLoading(true);
    try {
      if (mode === 'stepExecution') {
        const data = await fetchByStepExecution(stepExecutionId);
        const list = Array.isArray(data?.sessions) ? data.sessions : [];
        setSessions(list);
        setStepInfo({
          workflowExecutionId: data?.workflowExecutionId,
          workflowStepExecutionId: data?.workflowStepExecutionId,
          stepType: data?.stepType,
          taskName: data?.taskName
        });
        if (list.length > 0) {
          setActiveSessionIdRaw((prev) => {
            if (prev && list.some((s) => s.id === prev)) return prev;
            return list[0].id;
          });
        } else {
          setActiveSessionIdRaw(null);
        }
      } else {
        const data = await fetchInbox(inboxParamsRef.current);
        const list = Array.isArray(data?.sessions) ? data.sessions : [];

        // 初始化 seenMessageCounts（新 session 預設為已讀）
        list.forEach((s) => {
          if (seenMessageCountsRef.current[s.id] === undefined) {
            seenMessageCountsRef.current[s.id] = s.messageCount ?? 0;
          }
        });

        setSessions(list);
        setInboxMeta({
          total: data?.total ?? list.length,
          page: data?.page ?? 1,
          pageSize: data?.pageSize ?? 50
        });
        if (list.length > 0) {
          setActiveSessionIdRaw((prev) => {
            if (prev && list.some((s) => s.id === prev)) return prev;
            return list[0].id;
          });
        } else {
          setActiveSessionIdRaw(null);
        }
        // 重載後清除 newMessagesForActive（訊息已是最新）
        setNewMessagesForActive(false);
      }
    } catch (error) {
      console.error('[useChatBotConversation] 載入失敗:', error);
      antdMessage.error(loadFailedFallback);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, stepExecutionId, enabled, loadFailedFallback]);

  // ──────────────────────────────────────────────
  // 後台輪詢（inbox 模式）
  // ──────────────────────────────────────────────
  const doPoll = useCallback(async () => {
    if (!enabled || mode !== 'inbox') return;
    try {
      const params = { ...inboxParamsRef.current };
      const data = await fetchInboxMeta(params);
      const metaList = Array.isArray(data?.sessions) ? data.sessions : [];
      if (metaList.length === 0) return;

      // ── 1. 偵測全新 session（在目前列表中找不到的 ID）──
      const currentIds = new Set(sessionsRef.current.map((s) => s.id));
      const newSessionMetas = metaList.filter((m) => !currentIds.has(m.id));

      if (newSessionMetas.length > 0) {
        // 全量重載，取得完整 session 資料
        const freshData = await fetchInbox(inboxParamsRef.current);
        const freshList = Array.isArray(freshData?.sessions) ? freshData.sessions : [];
        const newIds = newSessionMetas.map((m) => m.id);

        // 新 session 預設未讀（seenMessageCounts = 0）
        newIds.forEach((id) => {
          seenMessageCountsRef.current[id] = 0;
        });

        setSessions(freshList);

        // 標記未讀數
        setUnreadCounts((prev) => {
          const next = { ...prev };
          newSessionMetas.forEach((m) => {
            if (m.messageCount) next[m.id] = m.messageCount;
          });
          return next;
        });

        // 記錄新 session IDs 觸發入場動畫，8 秒後自動清除
        setNewSessionIds((prev) => new Set([...prev, ...newIds]));
        setTimeout(() => {
          setNewSessionIds((prev) => {
            const next = new Set(prev);
            newIds.forEach((id) => next.delete(id));
            return next;
          });
        }, 8000);

        // 顯示通知
        notification.info({
          message: `收到 ${newSessionMetas.length} 個新對話`,
          description: newSessionMetas
            .map((m) => m.contactName || m.waId || '新訪客')
            .join('、'),
          placement: 'bottomRight',
          duration: 6,
          key: `new-sessions-${Date.now()}`
        });

        return; // 全量重載已涵蓋訊息計數更新，直接返回
      }

      // ── 2. 偵測現有 session 的新訊息（原本邏輯）──
      const currentActiveId = activeSessionIdRef.current;
      let hasAnyNewMessages = false;
      const newUnreadDelta = {};
      const sessionUpdates = {}; // { [id]: { messageCount, lastMessagePreview, state, updatedAt } }

      metaList.forEach((meta) => {
        const seen = seenMessageCountsRef.current[meta.id] ?? meta.messageCount ?? 0;
        const current = meta.messageCount ?? 0;
        if (current > seen) {
          const delta = current - seen;
          newUnreadDelta[meta.id] = delta;
          hasAnyNewMessages = true;
          sessionUpdates[meta.id] = {
            messageCount: current,
            lastMessagePreview: meta.lastMessagePreview,
            state: meta.state,
            updatedAt: meta.updatedAt
          };
        }
      });

      if (!hasAnyNewMessages) return;

      // 更新 session 列表的 metadata（不觸碰 messages 陣列，避免畫面跳動）
      setSessions((prev) =>
        prev.map((s) => {
          if (sessionUpdates[s.id]) {
            return {
              ...s,
              messageCount: sessionUpdates[s.id].messageCount,
              lastMessagePreview: sessionUpdates[s.id].lastMessagePreview,
              state: sessionUpdates[s.id].state,
              updatedAt: sessionUpdates[s.id].updatedAt
            };
          }
          return s;
        })
      );

      // 更新 unreadCounts
      setUnreadCounts((prev) => {
        const next = { ...prev };
        Object.keys(newUnreadDelta).forEach((id) => {
          next[id] = (next[id] || 0) + newUnreadDelta[id];
        });
        return next;
      });

      // 如果 active session 有新訊息
      if (currentActiveId && newUnreadDelta[currentActiveId]) {
        // 若用戶目前沒有在輸入，自動重載完整訊息
        if (!inputTextRef.current?.trim()) {
          // 靜默重載（不顯示 loading spinner）
          try {
            const freshData = await fetchInbox(inboxParamsRef.current);
            const freshList = Array.isArray(freshData?.sessions) ? freshData.sessions : [];
            setSessions(freshList);
            // 重載後：active session 已讀，清空 unread
            seenMessageCountsRef.current[currentActiveId] =
              freshList.find((s) => s.id === currentActiveId)?.messageCount ?? 0;
            setUnreadCounts((prev) => {
              const next = { ...prev };
              delete next[currentActiveId];
              return next;
            });
            setNewMessagesForActive(false);
          } catch {
            // 靜默重載失敗時改為顯示提示
            setNewMessagesForActive(true);
          }
        } else {
          // 用戶正在輸入：只顯示提示，不自動重載（避免打斷輸入）
          setNewMessagesForActive(true);
        }
      }
    } catch (error) {
      // 輪詢失敗靜默處理，不打擾用戶
      console.warn('[useChatBotConversation] 輪詢失敗:', error);
    }
  }, [enabled, mode]);

  // 啟動 / 停止輪詢
  useEffect(() => {
    if (mode !== 'inbox' || !enabled || !pollIntervalMs) return;
    const timer = setInterval(doPoll, pollIntervalMs);
    return () => clearInterval(timer);
  }, [mode, enabled, pollIntervalMs, doPoll]);

  // ──────────────────────────────────────────────
  // 選擇 session → 標記為已讀
  // ──────────────────────────────────────────────
  const setActiveSessionId = useCallback((sessionId) => {
    setActiveSessionIdRaw(sessionId);
    if (!sessionId) return;
    // 標記為已讀
    setSessions((prev) => {
      const s = prev.find((x) => x.id === sessionId);
      if (s) seenMessageCountsRef.current[sessionId] = s.messageCount ?? 0;
      return prev;
    });
    setUnreadCounts((prev) => {
      if (!prev[sessionId]) return prev;
      const next = { ...prev };
      delete next[sessionId];
      return next;
    });
    setNewMessagesForActive(false);
  }, []);

  // 手動標記某 session 為已讀（供外部呼叫）
  const markSessionRead = useCallback((sessionId) => {
    setSessions((prev) => {
      const s = prev.find((x) => x.id === sessionId);
      if (s) seenMessageCountsRef.current[sessionId] = s.messageCount ?? 0;
      return prev;
    });
    setUnreadCounts((prev) => {
      if (!prev[sessionId]) return prev;
      const next = { ...prev };
      delete next[sessionId];
      return next;
    });
    if (activeSessionIdRef.current === sessionId) setNewMessagesForActive(false);
  }, []);

  // 計算總未讀數
  const totalUnread = useMemo(
    () => Object.values(unreadCounts).reduce((acc, n) => acc + n, 0),
    [unreadCounts]
  );

  // ──────────────────────────────────────────────
  // Active session & combined messages
  // ──────────────────────────────────────────────
  const activeSession = useMemo(
    () => sessions.find((s) => s.id === activeSessionId) || null,
    [sessions, activeSessionId]
  );

  const combinedMessages = useMemo(
    () => buildCombinedMessages(activeSession, interventionMessages),
    [activeSession, interventionMessages]
  );

  // ──────────────────────────────────────────────
  // Admin 介入發送
  // ──────────────────────────────────────────────
  const handleSendIntervention = useCallback(async () => {
    const text = (inputText || '').trim();
    if (!text || !activeSession) return;
    if (!activeSession.waId) {
      antdMessage.error('此對話缺少 WhatsApp 號碼，無法發送');
      return;
    }
    setSending(true);
    const tempKey = `int-${Date.now()}`;
    const optimistic = {
      key: tempKey,
      role: 'assistant',
      content: text,
      at: new Date().toISOString(),
      intervention: true,
      sending: true
    };
    setInterventionMessages((prev) => ({
      ...prev,
      [activeSession.id]: [...(prev[activeSession.id] || []), optimistic]
    }));
    setInputText('');

    try {
      await sendIntervention({
        waId: activeSession.waId,
        message: text,
        workflowInstanceId:
          activeSession.workflowExecutionId || stepInfo?.workflowExecutionId
      });
      setInterventionMessages((prev) => ({
        ...prev,
        [activeSession.id]: (prev[activeSession.id] || []).map((m) =>
          m.key === tempKey ? { ...m, sending: false, sent: true } : m
        )
      }));
      antdMessage.success('訊息已發送');
    } catch (error) {
      console.error('[useChatBotConversation] 發送失敗:', error);
      setInterventionMessages((prev) => ({
        ...prev,
        [activeSession.id]: (prev[activeSession.id] || []).map((m) =>
          m.key === tempKey ? { ...m, sending: false, failed: true } : m
        )
      }));
      antdMessage.error('訊息發送失敗');
    } finally {
      setSending(false);
    }
  }, [inputText, activeSession, stepInfo]);

  // ──────────────────────────────────────────────
  // Reset
  // ──────────────────────────────────────────────
  const reset = useCallback(() => {
    setSessions([]);
    setStepInfo(null);
    setActiveSessionIdRaw(null);
    setInterventionMessages({});
    setInputText('');
    setUnreadCounts({});
    setNewMessagesForActive(false);
    seenMessageCountsRef.current = {};
  }, []);

  return {
    loading,
    sessions,
    stepInfo,
    inboxMeta,
    activeSessionId,
    setActiveSessionId,
    activeSession,
    combinedMessages,
    interventionMessages,
    inputText,
    setInputText,
    sending,
    loadSessions,
    handleSendIntervention,
    reset,
    // unread
    unreadCounts,
    totalUnread,
    newMessagesForActive,
    markSessionRead,
    // 新 session 動畫
    newSessionIds
  };
};

export default useChatBotConversation;
